/**
 * Resources Hooks
 * React Query hooks for fetching user resources
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { resourcesApi } from '@/lib/api/resources';
import type { ResourceType } from '@/types/resources';

/**
 * Hook to fetch user resource summary
 */
export function useUserResourceSummary(userId: string | null) {
  return useQuery({
    queryKey: ['user-resources-summary', userId],
    queryFn: () => resourcesApi.getUserSummary(userId!),
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user artifacts
 */
export function useUserArtifacts(
  userId: string | null,
  params?: {
    subtype?: string;
    include_expired?: boolean;
    include_urls?: boolean;
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: ['user-artifacts', userId, params],
    queryFn: () => resourcesApi.getUserArtifacts(userId!, params),
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user library files
 */
export function useUserLibrary(
  userId: string | null,
  params?: {
    category?: string;
    include_urls?: boolean;
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ['user-library', userId, params],
    queryFn: () => resourcesApi.getUserLibrary(userId!, params),
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user temp files
 */
export function useUserTemp(
  userId: string | null,
  params?: {
    include_urls?: boolean;
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ['user-temp', userId, params],
    queryFn: () => resourcesApi.getUserTemp(userId!, params),
    enabled: !!userId,
  });
}

/**
 * Hook to delete a user resource
 */
export function useDeleteResource(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resourceId: string) => resourcesApi.deleteUserResource(userId, resourceId),
    onSuccess: () => {
      // Invalidate all resource queries for this user
      queryClient.invalidateQueries({ queryKey: ['user-resources-summary', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-artifacts', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-library', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-temp', userId] });
      // Also invalidate system stats
      queryClient.invalidateQueries({ queryKey: ['resource-system-stats'] });
    },
  });
}

/**
 * Hook to delete all resources for a user
 */
export function useDeleteAllUserResources(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: { resource_type?: ResourceType; dry_run?: boolean }) =>
      resourcesApi.deleteAllUserResources(userId, params),
    onSuccess: () => {
      // Invalidate all resource queries
      queryClient.invalidateQueries({ queryKey: ['user-resources-summary', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-artifacts', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-library', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-temp', userId] });
      queryClient.invalidateQueries({ queryKey: ['resource-system-stats'] });
    },
  });
}
